import { memoize } from "@use/decorators";
import { loadJs } from "@use/load-js";

import { urls } from "@courses-pm2/api/src/ts/config";
import {
  getStandardPageCount,
  getTextContent,
} from "@courses-pm2/api/src/ts/utils";

import {
  CaptionedContentElementData,
  CaptionedContentElementModel,
} from "@courses-pm2/api/src/ts/models/elements/CaptionedContentElementModel";
import { gGateway } from "@utils/gateway";

/**
 * Enumeration of different available targets when iframes cannot be embedded
 * into the page content.
 */
export enum IframeTargets {
  LIGHTBOX,
  WINDOW,
}

/**
 * Specification of data needed to instantiate a new AccordionModel.
 */
export type IframeData = {
  [K in
    | "aspectRatio"
    | "autoActivate"
    | "inPageEmbed"
    | "target"
    | "maxSize"
    | "minSize"
    | "previewImage"
    | "src"
    | "needsAutoGeneratedPreviewImage"]: IframeModel[K];
};

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class IframeModel extends CaptionedContentElementModel {
  /**
   * Specifies the desired aspectRatio between width and height. These
   * aspectRatio can be used to generate responsively resized iframes.
   */
  public readonly aspectRatio: {
    width: number;
    height: number;
  };

  /**
   * Specifies if the frame should automatically be activated when it's
   * rendered (if not, the preview image should be displayed until the user
   * clicks on that button).
   */
  public readonly autoActivate?: boolean;

  /**
   * Specifies if the iframe can be embedded in-page (assuming that the user's
   * device meets the minSize requirements). Otherwise the iframe should be
   * opened in the desired target.
   */
  public readonly inPageEmbed?: boolean;

  /**
   * Specifies how to open the iframe, if in-page embedding is not possible.
   */
  public readonly target: IframeTargets;

  /**
   * Specifies the maximum size that this iframe can be scaled up to (to limit
   * the size of certain iframes).
   */
  public readonly maxSize?: {
    width?: number;
    height?: number;
  };

  /**
   * Specifies the minimum size that this iframe needs to have available in
   * order to allow embedding on the page (if this requirement isn't met, then
   * the preview image should be embedded instead and the iframe opened in
   * the desired target on click).
   */
  public readonly minSize: {
    width: number;
    height: number;
  };

  /**
   * Defines whether the previewImage for this model must be autoGenerated
   */
  public readonly needsAutoGeneratedPreviewImage: boolean;

  /**
   * Optionally contains an absolute url to an image that should be used as a
   * preview image.
   */
  public previewImage?: string;

  /**
   * Specifies the absolute URL to the page that should be opened in the
   * iframe (but do remember to run it through gProxy.forwardTo before opening
   * to ensure any authentication is applied).
   */
  public readonly src: string;

  /**
   * Apply data given when a new model is being instantiated.
   */
  constructor(initial: IframeData & CaptionedContentElementData) {
    super(initial);

    this.autoActivate = initial.autoActivate;
    this.inPageEmbed = initial.inPageEmbed;
    this.target = initial.target;
    this.maxSize = initial.maxSize;
    this.minSize = initial.minSize;
    this.previewImage = initial.previewImage;
    this.needsAutoGeneratedPreviewImage =
      initial.needsAutoGeneratedPreviewImage;
    this.aspectRatio = initial.aspectRatio;
    this.src = initial.src;
  }

  /**
   * If a preview image was supplied, then use that one directly - otherwise
   * fallback to a screengrab of the embedded page.
   */
  @memoize()
  public async getPreviewImageUrl(
    requestedAspectRatio?: number,
  ): Promise<string | undefined> {
    if (this.previewImage) {
      return this.previewImage;
    }

    // Wait for the ExportService to generate the desired screenshot
    try {
      // Wait for the gExport service script to be loaded
      await loadJs(urls.gExport);

      // Bail out if gExport is unavailable
      // tslint:disable-next-line no-typeof-undefined
      if (typeof window.gExport === "undefined") {
        return;
      }

      window.gExport.utils.registerRequestInterceptor(
        (input: RequestInfo | URL, init?: RequestInit) => {
          if (input instanceof Request) {
            return new Request(gGateway(input.url), input);
          } else if (input instanceof URL) {
            return new Request(gGateway(input.toString()), init);
          } else {
            return new Request(gGateway(input), init);
          }
        },
      );

      // Determine the desired aspect ratio for the screenshot
      const aspectRatio =
        requestedAspectRatio ||
        this.aspectRatio.height / this.aspectRatio.width;

      // Determine if the screenshot should be generated using hd or sd
      // resolution
      const preset = window.devicePixelRatio >= 2 ? "hd" : "sd";

      // Generate the raw url, which we're going to grab a screenshot from
      const url = this.src.replace(
        /(\?|\&)(UserId)=[^\&\#]+/gi,
        "$1$2=exportservice",
      );

      // tslint:disable-next-line await-promise
      const screenshot = await window.gExport.convert({
        aspectRatio,
        format: "jpg",
        input: new URL(gGateway.forward(url), window.location.href).toString(),
        preset,

        // Generate screenshot using iframe embed-mode (as that's exactly
        // how the url will be embedded in this client, it will provide a
        // more realistic output)
        embed: true,

        // Cache screengrab for up to 1 week
        cache: 7 * 24 * 60 * 60 * 1000,
      });

      // Cache and return the url
      return (this.previewImage = screenshot.url);
    } catch (err) {
      // Silently ignore errors when generating screenshot url a nd bail
      // out
      return undefined;
    }
  }

  /**
   * @inheritdoc
   *
   * Due to the nature of the calculation of standard pages, media elements
   * aren't counted on their own. We do however include the caption of the
   * element in the calculations.
   */
  // tslint:disable-next-line: completed-docs
  @memoize()
  public getStandardPageCount(): number {
    return getStandardPageCount(getTextContent(this.getCaptionText()));
  }
}
